/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { replacementDataType } from '../../insights-data.interface';
import { CommonModule } from '@angular/common';
import {
  type GraphTooltipParams,
  InsightsDataService,
} from '../../insights-data.service';
import { Subject, takeUntil } from 'rxjs';
import { AccessibilityService } from '../../../../../../core/services/accessibility.service';
@Component({
  selector: 'app-replacement-behavior-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './replacement-behavior-graph.component.html',
})
export class ReplacementBehaviorGraphComponent implements OnDestroy {
  showTrendLine = false;
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  private readonly destroy$ = new Subject<void>();
  totalCharts = 0;
  isShow = true;
  maxLength = 25;
  splitRange = 15;
  behaviorId = 0;
  private readonly printTextColor = '#000000';
  private currentGraphData: replacementDataType | null = null;
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  @Input() set graphData(replacementData: replacementDataType) {
    this.currentGraphData = replacementData;
    this.behaviorId = replacementData.caseBehaviorId ?? 0;
    this.showTrendLine = replacementData.showTrendLine ?? false;
    this.setGraphOptionsDefault(replacementData, false);
    this.setGraphOptionsDefault(replacementData, true);
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly insightsService: InsightsDataService,
    private readonly accessibilityService: AccessibilityService
  ) {
    this.accessibilityService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyThemeToExistingCharts();
      });
  }

  setGraphOptionsDefault(
    replacementData: replacementDataType,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      this.renderPrintChart(replacementData);
      return;
    }

    this.renderMainChart(replacementData);
  }

  private renderPrintChart(replacementData: replacementDataType): void {
    this.chartInstances = [];
    this.totalCharts = 1;
    this.allChartsRendered = false;
    this.renderedChartsCount = 0;
    this.weekGraphData = [];
    const weekData = replacementData;
    const hasValidData =
      weekData.frequency.some(val => val !== null) ||
      weekData.treatmentPhases.length > 0;

    this.weekGraphData.push(weekData);

    const graphOptions = this.setGraphOptions(weekData, true);
    setTimeout(() => {
      const chartDom = document.getElementById(
        `chart-container-replacement-${this.behaviorId}-${0}`
      );
      if (chartDom) {
        const chartInstance = echarts.init(chartDom);
        chartInstance.setOption(graphOptions);

        chartInstance.on('finished', () => {
          this.renderedChartsCount++;

          if (this.renderedChartsCount === this.totalCharts) {
            this.allChartsRendered = true;

            this.exportAllGraphs();
          }
        });

        if (hasValidData) {
          this.chartInstances.push(chartInstance);
        }
      }
    }, 100);
  }

  private renderMainChart(replacementData: replacementDataType): void {
    const chartOptions = this.setGraphOptions(replacementData);

    const replacementDom = this.elementRef.nativeElement.querySelector(
      '#replacement-behavior'
    );

    if (replacementDom) {
      const chartInstance = echarts.init(replacementDom);
      this.chartInstance = chartInstance;
      chartInstance.setOption(chartOptions);
      chartInstance.on('finished', () => {
        this.emitChartAsImage();
      });
    }
  }

  setGraphOptions(replacementData: replacementDataType, forPrint = false) {
    const textColor = forPrint
      ? this.printTextColor
      : this.getThemeChartTextColor();

    const trendlineFrequency = this.insightsService.generateTrendlineArray(
      replacementData.xLabels,
      replacementData.frequency
    );

    const markLines = replacementData.treatmentPhases?.map(item => {
      const color = this.insightsService.getRandomColor();
      return {
        xAxis: item.treatmentPhaseDate,
        label: {
          show: true,
          formatter: item.treatmentPhaseName,
          position: 'insideEndTop',
          color,
        },
        lineStyle: {
          type: 'solid',
          color,
          width: 2,
        },
        tooltip: {
          show: true,
          formatter: () =>
            `Treatment Phase <br>${item.dateLabel} - ${item.treatmentPhaseName}`,
        },
      };
    });

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const replacementGraphOption = {
      title: {
        text: 'Replacement Behavior',
        left: 'center',
        textStyle: {
          color: textColor,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: GraphTooltipParams) =>
          this.insightsService.formatItemTooltipWithWeekday(params),
      },
      legend: {
        data: ['Frequency', 'Trendline Frequency'],
        left: 'left',
        bottom: 40,
        textStyle: {
          color: textColor,
        },
        pageTextStyle: {
          color: textColor,
        },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: replacementData.xLabels,
        axisLabel: {
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          color: textColor,
        },
        nameLocation: 'center',
        nameTextStyle: {
          padding: [0, 0, 8, 0],
          color: textColor,
        },
      },
      series: [
        {
          name: 'Frequency',
          type: 'line',
          data: replacementData.frequency,
          symbolSize: 10,
          itemStyle: {
            color: '#47CD89',
          },
          label: {
            show: true,
            position: 'top',
            color: '#47CD89',
            fontSize: 12,
            formatter: '{c}',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        {
          name: 'Target Line',
          type: 'line',
          markLine: {
            data: markLines,
            symbol: 'none',
          },
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Frequency',
                type: 'line',
                data: trendlineFrequency,
                symbol: 'none',
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
      graphic: {
        elements: [
          {
            type: 'rect',
            left: 0,
            top: 0,
            shape: { width: 0, height: 0 },
            draggable: true,
            ondrag: function (dx: number) {
              const chartContainer = document.getElementById('chart-container');
              if (!chartContainer) return;
              const chartInstance = echarts.getInstanceByDom(chartContainer);
              if (!chartInstance) return;

              const option = chartInstance.getOption() as any;
              option['dataZoom'][0].start += dx * 0.05;
              option['dataZoom'][0].end = option['dataZoom'][0].start + 7;
              chartInstance.setOption(option);
            },
          },
        ],
      },
    };

    return replacementGraphOption;
  }

  private getThemeChartTextColor(): string {
    const colorValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--gray-600')
      .trim();

    return colorValue ? `rgb(${colorValue})` : '#475467';
  }

  private applyThemeToExistingCharts(): void {
    const graphData = this.currentGraphData;
    if (!graphData) {
      return;
    }

    if (this.chartInstance) {
      this.chartInstance.setOption(this.setGraphOptions(graphData), true);
    }

    this.chartInstances.forEach(chart => {
      chart.setOption(this.setGraphOptions(graphData, true), true);
    });
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });

    this.allChartImage.emit(images);
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
